/**
 * @engine/graph — Generic Graph Framework
 *
 * Provides core data structures and utilities for visual node editors.
 * Used by Redprint (visual scripting), VFX graphs, and other node-based systems.
 *
 * @example
 * ```ts
 * import { Graph, serializeGraph, deserializeGraph } from '@engine/graph';
 *
 * // Create a graph
 * const graph = new Graph();
 * const nodeA = graph.addNode('math.add', { x: 0, y: 0 });
 * const nodeB = graph.addNode('math.multiply', { x: 200, y: 0 });
 * graph.connect({ nodeId: nodeA, portId: 'result' }, { nodeId: nodeB, portId: 'a' });
 *
 * // Serialize to JSON
 * const json = graphToJSON(graph.toData());
 *
 * // Deserialize
 * const data = graphFromJSON(json);
 * const restored = Graph.fromData(data);
 * ```
 */

// ---- Core types ----
export type {
  Vec2,
  Vec2Like,
  Vec2Mut,
  PortRef,
  Edge,
  BaseNodeInstance,
  BaseGraphVariable,
  BaseGraphData,
  GraphMetadata,
  PortDirection,
  PortKind,
  BasePortDefinition,
  BaseNodeDefinition,
  NodeLayoutDirection,
  StickyNote,
  NodeGroup,
  GraphDiagnostic,
  // Context support
  ContextDefinition,
  ContextInstance,
} from './types';

// ---- Graph model ----
export { Graph, uid, resetUidCounter } from './Graph';

// ---- Port control schema (declarative inline-editor descriptors) ----
export { resolvePortControl } from './PortControl';
export type { PortControl, PortControlKind } from './PortControl';

// ---- Registration bootstrap ----
export { createBootstrap } from './bootstrap';

// ---- Serialization ----
export {
  serializeGraph,
  deserializeGraph,
  graphToJSON,
  graphFromJSON,
} from './GraphSerializer';
export type {
  SerializedGraph,
  SerializedNode,
  SerializedEdge,
  SerializedVariable,
  SerializeOptions,
  DeserializeOptions,
  JsonSerializeOptions,
} from './GraphSerializer';

// ---- Validation ----
export { GraphValidator, topologicalSort } from './GraphValidator';
export type {
  ValidationResult,
  NodeDefinitionResolver,
  ConnectionCheck,
  CanConnectFn,
  ValidatorOptions,
} from './GraphValidator';

// ---- Registry foundation ----
// Generic registry base shared by node-based registries (Redprint NodeRegistry,
// VFX VFXNodeRegistry, ...). Lives here (not @engine/core) because core depends
// on @engine/redprint, so a core-hosted base would create a core↔redprint cycle.
export { BaseRegistry } from './BaseRegistry';
export type { IRegistry, RegistryEntry } from './BaseRegistry';
